import {DayOfWeek, LocalDate} from '@js-joda/core';
import {useSnackbar} from 'notistack';
import React, {useCallback, useEffect, useMemo, useReducer, useState} from 'react';
import useApiFetch, {apiEndpoint} from '../Auth/Api';
import InlineLoadingIndicator from '../LoadingIndicator/InlineLoadingIndicator';
import {useProjects} from '../Projects/ProjectsProvider';
import TableSchedule from './TableSchedule/TableSchedule';
import CapacityContextProvider from "./Utils/CapacityContextProvider";
import {
    ClientServiceTeam,
    compileSchedules,
    Employee,
    EmployeeActuals,
    Schedule
} from './Utils/EmployeeSchedulesCompiler';
import {employeeSchedulesReducer} from './Utils/EmployeeSchedulesReducer';
import {reasonsReducer} from './Utils/ReasonsReducer';
import ScheduleContextProvider from './Utils/ScheduleContextProvider';

const PersonalSchedule : React.FC = () => {
    const apiFetch = useApiFetch();
    const {projectsWithAllStatuses, detectAndLoadMissingProjects} = useProjects();
    const [employee, setEmployee] = useState<Employee | null>(null);
    const [employeeActuals, setEmployeeActuals] = useState<EmployeeActuals[] | null>(null);
    const [schedules, setSchedules] = useState<Schedule[] | null>(null);
    const [employeeSchedules, dispatchEmployeeSchedules] = useReducer(employeeSchedulesReducer, null);
    const [reasons, dispatchReasons] = useReducer(reasonsReducer, null);
    const [teams, setTeams] = useState<ClientServiceTeam[]>([]);
    const {enqueueSnackbar} = useSnackbar();

    const loadTeams = useCallback(async () => {
        const response = await apiFetch(new URL('/v1/scheduler/teams/', apiEndpoint).toString());

        if (!response.ok) {
            return enqueueSnackbar('Could not load team list', {variant: 'error'});
        }

        const teams = await response.json() as ClientServiceTeam[];
        setTeams(teams.sort((a, b) => a.abbreviation.localeCompare(b.abbreviation)));
    }, [apiFetch, enqueueSnackbar]);

    const startDate = useMemo(() => {
        const date = LocalDate.now();

        if (date.dayOfWeek() === DayOfWeek.SUNDAY) {
            return date.minusDays(7);
        }

        return date.minusDays(date.dayOfWeek().value() + 7);
    }, []);
    const currentWeek = useMemo(() => startDate.plusWeeks(1), [startDate]);

    const loadEmployee = useCallback(async () => {
        const response = await apiFetch(new URL('/v1/scheduler/employees/self', apiEndpoint).toString());

        if (!response.ok) {
            return enqueueSnackbar('Could not load employee', {variant: 'error'});
        }

        const employee = await response.json();
        setEmployee(employee);
    }, [apiFetch, enqueueSnackbar]);


    const loadEmployeeActuals = useCallback(async (employee : Employee) => {
        const url = new URL('/v1/scheduler/actuals', apiEndpoint);
        url.searchParams.set('dateStart', startDate.toString());
        url.searchParams.set('dateEnd', startDate.plusWeeks(7).toString());
        url.searchParams.set('employeeId', employee.id.toString());

        const response = await apiFetch(url.toString());

        if (!response.ok) {
            return enqueueSnackbar('Could not load actuals', {variant: 'error'});
        }

        const employeeActuals = await response.json() as EmployeeActuals[];
        setEmployeeActuals(employeeActuals);
    }, [apiFetch, startDate, enqueueSnackbar]);

    // Detect missing projects from actuals and load them
    useEffect(() => {
        if (employeeActuals) {
            detectAndLoadMissingProjects(employeeActuals);
        }
    }, [employeeActuals, detectAndLoadMissingProjects]);

    const loadSchedules = useCallback(async (employee : Employee) => {
        const url = new URL('/v1/scheduler/schedules', apiEndpoint);
        url.searchParams.set('date', startDate.toString());
        url.searchParams.set('employeeId', employee.id.toString());

        const response = await apiFetch(url.toString());

        if (!response.ok) {
            return enqueueSnackbar('Could not load schedules', {variant: 'error'});
        }

        const schedules = await response.json();
        setSchedules(schedules);
    }, [apiFetch, startDate, enqueueSnackbar]);

    const loadReasons = useCallback(async (employee : Employee) => {
        const url = new URL('/v1/scheduler/reasons', apiEndpoint);
        url.searchParams.set('employeeId', employee.id.toString());

        const response = await apiFetch(url.toString());

        if (!response.ok) {
            return enqueueSnackbar('Could not load reasons', {variant: 'error'});
        }

        const reasons = await response.json();
        dispatchReasons({type: 'initialLoad', payload: {reasons}});
    }, [apiFetch, enqueueSnackbar]);

    useEffect(() => {
        loadEmployee().catch(e => console.error('error loading Employee', e));
        loadTeams().catch(e => console.error('error loading Teams', e));
    }, [loadEmployee, loadTeams]);

    useEffect(() => {
        if (!employee) {
            return;
        }

        loadEmployeeActuals(employee).catch(e => console.error('error loading Actuals', e));
        loadSchedules(employee).catch(e => console.error('error loading Schedules', e));
        loadReasons(employee).catch(e => console.error('error loading Reasons', e));
    }, [employee, loadEmployeeActuals, loadSchedules, loadReasons]);

    useEffect(() => {
        if (!projectsWithAllStatuses || !employee || !schedules) {
            return;
        }

        const employeeSchedules = compileSchedules(startDate, [employee], projectsWithAllStatuses, schedules, employeeActuals);
        dispatchEmployeeSchedules({type: 'initialLoad', payload: {employeeSchedules}});
    }, [startDate, projectsWithAllStatuses, employee, schedules, employeeActuals]);

    if (!projectsWithAllStatuses || !employee || !employeeSchedules) {
        const value = (projectsWithAllStatuses ? 1 : 0) + (employee ? 1 : 0) + (schedules ? 1 : 0);

        return (
            <InlineLoadingIndicator
                variant={value === 0 ? 'indeterminate' : 'determinate'}
                value={value / 3 * 100}
            />
        );
    }

    return (
        <CapacityContextProvider
            startDate={startDate}
            associateIDs={[employee.id]}
        >
            <ScheduleContextProvider
                employees={[employee]}
                dispatchEmployeeSchedules={dispatchEmployeeSchedules}
                dispatchReasons={dispatchReasons}
            >
                <TableSchedule
                    startDate={startDate}
                    currentWeek={currentWeek}
                    employeeSchedule={employeeSchedules[0]}
                    reasons={reasons}
                    actualsLoaded={Boolean(employeeActuals)}
                    teams={teams}
                />
            </ScheduleContextProvider>
        </CapacityContextProvider>
    );
};

export default PersonalSchedule;
